import ConfirmPopover from '@/components/common/ConfirmPopover';
import List from '@/components/common/List';
import { useApplication } from '@/hooks/useApplication';
import { useMetadata } from '@/hooks/useMetadata';
import { useSubroutine } from '@/hooks/useSubroutine';
import { useUpdateNodes } from '@/hooks/useUpdateNodes';
import { Prompt } from '@/types/prompt';
import { SearchBlocksItem } from '@/types/search';
import { findSubroutine, getUpdatedNodes } from '@/util/application';
import { getNewPromptId, promptIsInUse } from '@/util/prompts';
import { isString } from '@/util/typeGuards';
import { ChevronRight, ExpandMore } from '@mui/icons-material';
import CircleIcon from '@mui/icons-material/Circle';
import ClearIcon from '@mui/icons-material/Clear';
import EditIcon from '@mui/icons-material/Edit';
import { Box, Button, IconButton, ListItem, ListItemText, ListSubheader, Stack, Tooltip } from '@mui/material';
import { FC, JSX, useState } from 'react';
import PromptEditor from './PromptEditor';

type PromptManagerProps = {
  promptGroupId: number;
  filteredPrompts: Prompt[] | undefined;
  onPromptSearch: (searchValue: SearchBlocksItem) => void;
};

const PromptManager: FC<PromptManagerProps> = (props) => {
  const { application, setApplication } = useApplication();
  const { metadata } = useMetadata();
  const { setUpdateNodes } = useUpdateNodes();
  const { selectedSubroutineId } = useSubroutine();
  const currentSubroutine = findSubroutine(application.subroutines, selectedSubroutineId);

  const [headerVisibility, setHeaderVisibility] = useState<Record<string, boolean>>({});
  const [selectedPromptId, setSelectedPromptId] = useState<number>();
  const [openEdit, setOpenEdit] = useState(false);

  const blankPrompt: Prompt = {
    id: getNewPromptId(application.prompts),
    file: '',
    label: '',
    path: application.promptGroups?.find((group) => group.id === props.promptGroupId)?.path || '',
    promptGroup: props.promptGroupId
  };

  const CustomListSubHeader: FC<{
    title: string;
    groupId: number;
  }> = (props) => {
    const isHeaderVisible = headerVisibility[props.title] !== false;

    return (
      <ListSubheader
        onClick={() => {
          setHeaderVisibility((prevVisibility) => ({
            ...prevVisibility,
            [props.title]: !isHeaderVisible
          }));
        }}
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          cursor: 'pointer',
          '&:hover': {
            backgroundColor: 'action.hover'
          }
        }}
      >
        <Box>{props.title}</Box>
        {isHeaderVisible ? <ExpandMore /> : <ChevronRight />}
      </ListSubheader>
    );
  };

  const renderPromptsWrapper = (prompts: Prompt[], title: string, groupId: number) => {
    const isHeaderVisible = headerVisibility[title] !== false;

    return (
      <Box key={`prompts-${title}-${groupId}`}>
        <CustomListSubHeader title={title} groupId={groupId} key={`prompt + ${title} + ${groupId}`} />
        {isHeaderVisible && renderPrompts(prompts)}
      </Box>
    );
  };

  const renderPrompts = (prompts: Prompt[]) =>
    prompts.map((prompt, index) => {
      const PromptIsUsed = promptIsInUse(application, metadata, currentSubroutine, prompt);
      const searchItem: SearchBlocksItem = {
        searchQuery: prompt.id.toString() ?? '',
        searchType: 'Prompt'
      };

      return (
        <ListItem key={`prompt.${prompt.id}.${index}`} sx={{ gap: 1 }}>
          <Tooltip
            onClick={() => PromptIsUsed && props.onPromptSearch(searchItem)}
            title={PromptIsUsed ? (PromptIsUsed === 'green' ? 'Prompt is in use.' : 'Prompt is in use elsewhere.') : ''}
          >
            <CircleIcon
              sx={{
                color: PromptIsUsed ?? 'transparent',
                cursor: PromptIsUsed ? 'pointer' : 'unset',
                pr: '5px'
              }}
            />
          </Tooltip>
          <ListItemText
            primary={
              <Box>
                {prompt.promptGroup
                  ? application.promptGroups?.find((group) => prompt.promptGroup === group.id)?.name + ' / '
                  : ''}
                {prompt.label || prompt.file}
              </Box>
            }
            secondary={
              <code>
                {prompt.path && (isString(prompt.path) ? prompt.path + '/' : `[${prompt.path?.name}]/`)}
                {prompt.file}
              </code>
            }
          />
          <IconButton
            onClick={() => {
              setSelectedPromptId(prompt.id);
              setOpenEdit(true);
            }}
          >
            <EditIcon />
          </IconButton>
          <ConfirmPopover
            text={
              <>
                Delete <code>{prompt.file}</code>?
              </>
            }
            onConfirm={() => {
              const newPrompts = application.prompts.filter((removedPrompt) => removedPrompt.id !== prompt.id);
              setApplication({ ...application, prompts: newPrompts });
              setUpdateNodes(getUpdatedNodes(prompt.id, 'prompt_list', application.blocks.details));
            }}
            icon={<ClearIcon />}
          />
        </ListItem>
      );
    });

  const getPrompts = () => {
    const result: JSX.Element[] = [];
    application.promptGroups?.forEach((group) => {
      const prompts = props.filteredPrompts?.filter((prompt) => prompt.promptGroup === group.id);
      prompts && result.push(renderPromptsWrapper(prompts, group.name, group.id));
    });

    const grouplessPrompts = props.filteredPrompts?.filter((prompt) => !prompt.promptGroup || prompt.promptGroup === 0);
    grouplessPrompts && result.push(renderPromptsWrapper(grouplessPrompts, 'All', 0));
    return result;
  };

  return (
    <Stack gap={1} alignItems='start'>
      <List sx={{ width: '100%' }}>
        {!props.promptGroupId ? getPrompts() : renderPrompts(props.filteredPrompts || [])}
      </List>
      <Button
        variant='contained'
        color='primary'
        sx={{ ml: 2 }}
        onClick={() => {
          setSelectedPromptId(undefined);
          setOpenEdit(true);
        }}
      >
        Add new
      </Button>
      <PromptEditor
        application={application}
        open={openEdit}
        onClose={() => setOpenEdit(false)}
        prompt={
          (selectedPromptId && application.prompts.find((prompt) => prompt.id === selectedPromptId)) || blankPrompt
        }
        onSave={(newPrompt) => {
          let newPrompts = application.prompts;
          if (application.prompts.find((prompt) => prompt.id === newPrompt.id)) {
            newPrompts = application.prompts.map((prompt) => (newPrompt.id === prompt.id ? newPrompt : prompt));
          } else {
            newPrompts.push(newPrompt);
          }
          setApplication({ ...application, prompts: newPrompts });
          setUpdateNodes(getUpdatedNodes(newPrompt.id, 'prompt_list', application.blocks.details));
        }}
      />
    </Stack>
  );
};

export default PromptManager;
